import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { Box, Button, Typography, Grid, Dialog, DialogTitle, DialogContent } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { MessageBox } from 'components/MessageBox/MessageBox';
import { TeamModal } from 'components/TeamModal';
import useHttp from 'hooks/useHttp';
import { scrollIntoView } from 'utils/scroll';
import { isLoading } from 'utils/misc';
import { DEFAULT_TEAM_URL } from 'api/team';
import { HTTP_METHODS } from 'constants';

const TITLE = 'Reactivate team';

export const ReactivateTeamModal = ({ open, onClose, onSuccess, teamId }) => {
    const topElementRef = useRef();

    const {
        data: dataReactivate,
        error: errorReactivate,
        clearData: clearDataReactivate,
        sendRequest: reactivateTeam,
        loading: loadingReactivate,
    } = useHttp();
    
    const {
        data: teamData,
        error: errorGetTeam,
        sendRequest: getTeamData,
        loading: loadingData,
    } = useHttp();

    const [messageType, setMessageType] = useState('none');
    const [responseMessage, setResponseMessage] = useState('');
    const [team, setTeam] = useState(null);
    const [isTeamModalOpen, setIsTeamModalOpen] = useState(false);

    useEffect(() => {
        (async () => {
            await getTeamData({
                url: `${DEFAULT_TEAM_URL}/${teamId}`,
            });
        })();
    }, [teamId]);

    useEffect(() => {
        if (errorGetTeam !== null) {
            setResponseMessage(
                errorGetTeam.response?.data?.message ||
                    'An error occurred'
            );
            setMessageType('error');
        }
    }, [errorGetTeam]);

    useEffect(() => {
        if (teamData) {
            setTeam(teamData);
        }
    }, [teamData]);

    useEffect(() => {
        clearDataReactivate();

        if (errorReactivate !== null) {
            setResponseMessage(errorReactivate.response?.data?.message || 'An error occurred');
            setMessageType('error');
        }

        if (dataReactivate) {
            setResponseMessage('Team reactivated successfully.');
            setMessageType('success');
            onSuccess();
        }
    }, [errorReactivate, dataReactivate, messageType]);

    const handleEditConfirmation = () => {
        setIsTeamModalOpen(true);
    };

    const handleTeamModalClose = () => {
        setIsTeamModalOpen(false);
        onClose();
    };

    const handleUpdateSuccess = () => {
        onSuccess('Team updated successfully.', 'success');
        onClose();
    };

    const handleReactivateConfirmation = async () => {
        const requestBody = {
            name: team.name,
            active: true,
        };

        await reactivateTeam({
            url: `${DEFAULT_TEAM_URL}/${teamId}`,
            method: HTTP_METHODS.PUT,
            data: requestBody,
        });

        scrollIntoView(topElementRef);
    };

    return (
        <Dialog open={open} onClose={onClose}>
            <DialogTitle style={{ textTransform: 'none' }}>
                {TITLE}
                <Button onClick={onClose} variant="CloseButton">
                    <CloseIcon />
                </Button>
            </DialogTitle>
            <DialogContent>
                <Grid width={500} ref={topElementRef}>
                    {messageType !== 'none' && (
                        <MessageBox messageType={messageType}>{responseMessage}</MessageBox>
                    )}
                    {messageType !== 'success' && (
                        <Grid item xs={5}>
                            <Typography>
                                Are you sure you want to reactivate the team?
                            </Typography>
                            <Box mt={2} display="flex" justifyContent="flex-end">
                                <Button
                                    onClick={onClose}
                                    variant="outlined"
                                    sx={{ marginRight: '8px' }}
                                    disabled={isLoading(loadingData, loadingReactivate)}
                                >
                                    Cancel
                                </Button>
                                <Button
                                    onClick={handleReactivateConfirmation}
                                    variant="contained"
                                    color="error"
                                    disabled={isLoading(loadingData, loadingReactivate)}
                                >
                                    Reactivate
                                </Button>
                            </Box>
                        </Grid>
                    )}
                    {messageType === 'success' && (
                        <Grid item xs={5}>
                            <Typography>Do you want to edit team?</Typography>
                            <Box mt={2} display="flex" justifyContent="flex-end">
                                <Button
                                    onClick={onClose}
                                    variant="outlined"
                                    sx={{ marginRight: '8px' }}
                                >
                                    Cancel
                                </Button>
                                <Button onClick={handleEditConfirmation} variant="contained">
                                    Edit
                                </Button>
                            </Box>
                        </Grid>
                    )}
                    {isTeamModalOpen && (
                        <TeamModal
                            open={isTeamModalOpen}
                            onClose={handleTeamModalClose}
                            onSuccess={handleUpdateSuccess}
                            teamId={teamId}
                        />
                    )}
                </Grid>
            </DialogContent>
        </Dialog>
    );
};

ReactivateTeamModal.propTypes = {
    open: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    onSuccess: PropTypes.func.isRequired,
    teamId: PropTypes.string.isRequired,
};
